import React, { useState, useRef } from 'react';
import { X, DollarSign } from 'lucide-react';
import { VentaActividad } from '../lib/supabase';
import { ventaActividadService } from '../services/ventaActividadService';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { DialogShell } from './ui/DialogShell';
import { MoneyInput } from './ui/MoneyInput';
import { Textarea } from './ui/Textarea';
import { reportarErrorInesperado } from '../utils/reportarErrorInesperado';
import { FormErrorAlert } from './ui/FormErrorAlert';
import { ComprobanteInput } from './comprobantes/ComprobanteInput';
import { MedioPagoFolio } from './comprobantes/MedioPagoFolio';
import { useComprobanteFormulario } from '../hooks/useComprobanteFormulario';
import { comprobanteService } from '../services/comprobanteService';
import { mensajeErrorDeNegocio } from '../utils/erroresDeNegocio';
import type { EntidadDinero } from '../types/comprobante';
import { fechaEnChile } from '../utils/dateHelpers';

interface RegistrarPagoPedidoModalProps {
  isOpen: boolean;
  onClose: () => void;
  pedido: VentaActividad | null;
  onSuccess: () => void;
  /** 'nested' cuando se abre sobre otra ventana ya abierta (ej. el detalle de pedido). */
  level?: 'base' | 'nested';
}

export const RegistrarPagoPedidoModal: React.FC<RegistrarPagoPedidoModalProps> = ({
  isOpen,
  onClose,
  pedido,
  onSuccess,
  level = 'base'
}) => {
  const [loading, setLoading] = useState(false);
  const [monto, setMonto] = useState<number>(0);
  const [fechaPago, setFechaPago] = useState(() => fechaEnChile(new Date()) ?? '');
  const [notas, setNotas] = useState('');

  // Entidad dueña de la actividad del pedido: es donde queda guardado el comprobante.
  const [entidad, setEntidad] = useState<EntidadDinero | null>(null);
  const actividadId = pedido?.actividad_id ?? null;
  React.useEffect(() => {
    if (!isOpen || !actividadId) return;
    let vigente = true;
    comprobanteService.getEntidadDeActividad(actividadId)
      .then((dueña) => { if (vigente) setEntidad(dueña); })
      .catch((err: unknown) => { if (vigente) reportarErrorInesperado(err, 'No pudimos preparar el comprobante del pago.'); });
    return () => { vigente = false; };
  }, [isOpen, actividadId]);

  const comprobante = useComprobanteFormulario({
    entidad,
    fechaMovimiento: fechaPago,
    permiteSubirDespues: true,
    pideMedioPago: true,
    sentidoDinero: 'ingreso',
  });
  const { reiniciar: reiniciarComprobante } = comprobante;

  // Inicializar monto al máximo pendiente cuando se abre el modal
  React.useEffect(() => {
    if (isOpen && pedido) {
      const pendiente = pedido.monto_total - pedido.monto_pagado;
      setMonto(pendiente);
      reiniciarComprobante();
      setFechaPago(fechaEnChile(new Date()) ?? '');
      setNotas('');
    }
  }, [isOpen, pedido, reiniciarComprobante]);

  // Hook centralizado para manejo de errores
  const { error, clearError, handleError } = useErrorHandler();

  // Función para detectar si hay cambios sin guardar
  const hasFormChanges = () => {
    if (!pedido) return false;
    const pendiente = pedido.monto_total - pedido.monto_pagado;
    return monto !== pendiente || comprobante.hayCambios || notas.trim() !== '';
  };

  const hasUnsavedChanges = loading || hasFormChanges();
  const closeRef = useRef<(() => void) | null>(null);
  const requestClose = () => closeRef.current?.();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!pedido) return;

    if (monto <= 0) {
      handleError('El monto debe ser mayor a 0');
      return;
    }

    const montoPendiente = pedido.monto_total - pedido.monto_pagado;
    if (monto > montoPendiente) {
      handleError('El monto no puede ser mayor al monto pendiente');
      return;
    }

    const errorComprobante = comprobante.validar();
    if (errorComprobante) {
      handleError(errorComprobante);
      return;
    }

    handleError('');
    setLoading(true);

    try {
      const respaldo = await comprobante.resolver();
      await ventaActividadService.registrarPago(pedido.id, {
        monto,
        modo_pago: comprobante.medioPago || 'efectivo',
        fecha: fechaPago,
        notas: notas || undefined,
        respaldo
      });

      comprobante.reiniciar();
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const deNegocio = mensajeErrorDeNegocio(err);
      if (deNegocio) handleError(deNegocio);
      else reportarErrorInesperado(err, 'No pudimos registrar el pago.');
    } finally {
      setLoading(false);
    }
  };

  
  if (!isOpen || !pedido) return null;

  const montoPendiente = pedido.monto_total - pedido.monto_pagado;
  const precioUnitario = pedido.cantidad > 0 ? pedido.monto_total / pedido.cantidad : 0;

  return (
    <DialogShell
      isOpen={isOpen}
      onClose={onClose}
      size="sm"
      level={level}
      hasUnsavedChanges={hasUnsavedChanges}
      closeRef={closeRef}
      header={(
        <div className="sticky top-0 z-10 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg">
              <DollarSign className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Registrar Pago</h2>
              <p className="text-emerald-100 text-sm">Pedido #{pedido.id.slice(0, 8)}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="text-white/80 hover:text-white transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      )}
    >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Monto Total:</span>
              <span className="font-semibold text-slate-900">
                ${pedido.monto_total.toLocaleString('es-CL')}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-600">Monto Pagado:</span>
              <span className="font-semibold text-emerald-600">
                ${pedido.monto_pagado.toLocaleString('es-CL')}
              </span>
            </div>
            <div className="border-t border-slate-200 pt-2 mt-2"></div>
            <div className="flex justify-between">
              <span className="text-slate-700 font-semibold">Monto Pendiente:</span>
              <span className="font-bold text-lg text-orange-600">
                ${montoPendiente.toLocaleString('es-CL')}
              </span>
            </div>
          </div>

          <FormErrorAlert error={error} onClear={clearError} />

          <ComprobanteInput comprobante={comprobante} deshabilitado={loading} />

          <div className="flex gap-3">
            <div className="flex-1 min-w-0">
              <MoneyInput
                label="Monto a Registrar"
                value={monto}
                onChange={setMonto}
                min={1}
                max={montoPendiente}
              />
            </div>
            <div className="w-40 flex-shrink-0">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Fecha de Pago
              </label>
              <input
                type="date"
                value={fechaPago}
                onChange={(e) => setFechaPago(e.target.value)}
                className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              />
            </div>
          </div>
          <div>
            {precioUnitario > 0 && (() => {
              const unidadesPendientes = Math.round(montoPendiente / precioUnitario);
              const unidadesSlider = precioUnitario > 0 ? Math.round(monto / precioUnitario) : 0;
              return unidadesPendientes >= 0 ? (
                <div className="mt-2">
                  <span className="text-xs text-slate-500">Elegir por unidades:</span>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {Array.from({ length: unidadesPendientes + 1 }, (_, i) => i).map((qty) => (
                      <button
                        key={qty}
                        type="button"
                        onClick={() => setMonto(qty * precioUnitario)}
                        className={`px-3 py-1 text-sm rounded border transition-colors ${
                          unidadesSlider === qty
                            ? 'bg-emerald-600 text-white border-emerald-600'
                            : 'bg-white text-slate-600 border-slate-300 hover:border-emerald-400 hover:text-emerald-700'
                        }`}
                      >
                        {qty}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null;
            })()}
          </div>

          <MedioPagoFolio comprobante={comprobante} deshabilitado={loading} />

          <Textarea
            label="Notas"
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={3}
            placeholder="Información adicional sobre este pago..."
          />

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={requestClose}
              className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-semibold"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || comprobante.subiendo}
              className="flex-1 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Registrando...' : 'Registrar Pago'}
            </button>
          </div>
        </form>
    </DialogShell>
  );
};
